import {
  SHStack,
  SHTable,
  SHTableBody,
  SHTableContainer,
  SHTableHead,
  SHTableRow,
  SHTypography,
} from "@components/design-systems";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import {
  CommentFeatureMatchColWidthPercent,
  CommentProductColWidthPercent,
} from "@pages/platform-analysis-v2/_id/constants";
import { RootState } from "@redux/store";
import { isNil } from "lodash";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { SummaryCommentHeaderCell } from "../comments/header-cell";
import { SummaryCommentRow } from "../comments/row";
import { getCommentColWidth } from "../comments/utils";
import { useParams } from "react-router";

export const SummaryAppendixPlatformComments = memo(() => {
  //#region Hooks & States
  const { analysisId } = useParams<{ analysisId: string }>();
  const analysisSteps = useSelector(
    (state: RootState) => state.platformAnalysis.analysisSteps,
  );
  const isCompleted = useSelector(
    (state: RootState) => state.platformAnalysis.isCompleted,
  );
  const analysisComments = useSelector(
    (state: RootState) =>
      state.platformAnalysis.platformAnalysis?.summary?.analysisComments,
  );
  const commentSettings = useSelector(
    (state: RootState) =>
      state.platformAnalysis.platformAnalysis?.summary?.specificCommentSetting,
  );

  const analysisCommentSteps = useMemo(() => {
    const displaySteps = analysisSteps?.steps?.filter(
      (item) => item !== AnalysisStep.Setup,
    );

    if (!isCompleted) return displaySteps;

    return displaySteps?.filter((step) => {
      switch (step) {
        case AnalysisStep.Feature:
          return commentSettings?.showFeature;
        case AnalysisStep.BusinessMetric:
          return commentSettings?.showBusinessMetric;
        case AnalysisStep.Fee:
          return commentSettings?.showFee;
        case AnalysisStep.Summary:
          return commentSettings?.showSummary;
        default:
          return true;
      }
    });
  }, [isCompleted, analysisSteps, commentSettings]);

  const columnsWidth = useMemo(
    () => getCommentColWidth(analysisCommentSteps?.length ?? 0),
    [analysisCommentSteps],
  );

  //#region Render
  if (isNil(analysisCommentSteps)) return <></>;

  return (
    <SHStack width={"100%"} gap={2}>
      <SHTypography variant="subtitle2">
        Platforms that were considered but not selected
      </SHTypography>
      <SHTableContainer>
        <SHTable>
          <SHTableHead>
            <SHTableRow>
              <SummaryCommentHeaderCell
                width={`${CommentProductColWidthPercent}%`}
              >
                Platform
              </SummaryCommentHeaderCell>
              <SummaryCommentHeaderCell
                width={`${CommentFeatureMatchColWidthPercent}%`}
              >
                Features match
              </SummaryCommentHeaderCell>
              {analysisCommentSteps?.map((step, stepIndex) => (
                <SummaryCommentHeaderCell
                  key={stepIndex}
                  width={`${columnsWidth}%`}
                >
                  {step}
                </SummaryCommentHeaderCell>
              ))}
            </SHTableRow>
          </SHTableHead>
          <SHTableBody>
            {analysisComments?.map((item, index) => {
              if (item.inFinalShortlist) return <></>;

              return (
                <SummaryCommentRow
                  key={`commentRow-${item.productId}-${index}`}
                  rowPath={`analysisComments.${index}`}
                  commentSettingFieldPath={"specificCommentSetting"}
                  analysisId={analysisId ?? ""}
                  columnWidth={columnsWidth}
                  rowData={item}
                  steps={analysisCommentSteps}
                  isAppendix={true}
                  isViewMode={!!isCompleted}
                />
              );
            })}
          </SHTableBody>
        </SHTable>
      </SHTableContainer>
    </SHStack>
  );
});
